import React, { useEffect, useMemo, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { backend_url } from "../App";
import { DEFAULT_SITE_MENU } from "../Constants/defaultSiteMenu";
import "./CSS/WebsiteMenu.css";
import SaveBar from "../Components/SaveBar/SaveBar";

const PREFIX = "/collections/";
const slugify = (s) => String(s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const slugOf = (path) => (path || "").startsWith(PREFIX) ? path.slice(PREFIX.length) : "";
let uid = 0;
const nextId = () => `m${++uid}`;

// Saved menu → editor rows (keeps any extra fields so nothing is lost on save)
const toEditor = (items) =>
  items.map((item) => ({
    ...item,
    _id: nextId(),
    type: item.priceFilter ? "price" : item.dropdown ? "dropdown" : "link",
    slug: slugOf(item.path),
    category: item.category ?? (item.path ? slugOf(item.path) : slugify(item.label)),
    hidden: !!item.hidden,
    open: false,
    dropdown: (item.dropdown || []).map((d) => ({ ...d, _id: nextId(), slug: slugOf(d.path), hidden: !!d.hidden })),
  }));

// Editor rows → menu saved for the website
const toSaved = (rows) =>
  rows.map(({ _id, type, slug, open, dropdown, ...rest }) => {
    const item = { ...rest, label: rest.label.trim(), hidden: !!rest.hidden };
    delete item.path; delete item.priceFilter; delete item.dropdown;
    if (type === "price") {
      item.priceFilter = true;
      delete item.category;
    } else if (type === "link") {
      item.path = PREFIX + slug;
      item.category = slug;
    } else {
      item.category = rest.category || slugify(rest.label);
      item.dropdown = dropdown.map(({ _id: _x, slug: s, ...d }) => ({
        ...d, label: d.label.trim(), path: PREFIX + s, hidden: !!d.hidden,
      }));
    }
    return item;
  });

const move = (list, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= list.length) return list;
  const copy = [...list];
  [copy[i], copy[j]] = [copy[j], copy[i]];
  return copy;
};

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${sessionStorage.getItem("adminToken")}`,
});

// Problems that stop saving, per menu item
const problemsFor = (r) => {
  const p = [];
  if (!r.label.trim()) p.push("Give this menu item a name");
  if (r.type === "link" && !r.slug) p.push("Choose which collection this link opens");
  if (r.type === "dropdown") {
    if (!r.dropdown.length) p.push("Add at least one dropdown entry");
    if (r.dropdown.some((d) => !d.label.trim())) p.push("Every dropdown entry needs a name");
    if (r.dropdown.some((d) => d.label.trim() && !d.slug)) p.push("Every dropdown entry needs a collection link");
  }
  return p;
};

const TYPES = [
  { value: "dropdown", icon: "☰", title: "Dropdown list", text: "Hover shows a list of collections (like Fabrics, Sarees)" },
  { value: "link",     icon: "🔗", title: "Single link",   text: "Opens one collection directly (like Vintage Collection)" },
  { value: "price",    icon: "₹",  title: "Price filter",  text: "Lets customers pick a price range" },
];

/* ── Small UI pieces ── */
const Switch = ({ on, onChange, label, small }) => (
  <button type="button" role="switch" aria-checked={on} aria-label={label}
    className={`wm-switch ${on ? "on" : ""} ${small ? "small" : ""}`} onClick={() => onChange(!on)} title={label}>
    <span className="wm-switch-knob" />
  </button>
);

const Icon = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>
);
const UP = "M18 15l-6-6-6 6", DOWN = "M6 9l6 6 6-6", TRASH = "M3 6h18M8 6V4h8v2m-9 0l1 14h8l1-14", PLUS = "M12 5v14M5 12h14";

const WebsiteMenu = () => {
  const [rows, setRows]           = useState([]);
  const [savedItems, setSavedItems] = useState([]);
  const [isCustom, setIsCustom]   = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [entrySearch, setEntrySearch] = useState("");
  const [newEntry, setNewEntry]   = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [productCounts, setProductCounts] = useState({});
  const [confirm, setConfirm]     = useState(null); // { title, text, yes, onYes }

  const applyItems = (items, custom, when) => {
    const editorRows = toEditor(items);
    setRows(editorRows);
    setSavedItems(toSaved(editorRows));
    setIsCustom(custom);
    setUpdatedAt(when || null);
    setSelectedId((id) => (editorRows.some((r) => r._id === id) ? id : editorRows[0]?._id || null));
  };

  const load = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${backend_url}/api/site-menu`);
      const data = await res.json();
      const custom = !!(data.success && Array.isArray(data.items) && data.items.length);
      applyItems(custom ? data.items : DEFAULT_SITE_MENU, custom, data.updatedAt);
    } catch {
      toast.error("Could not load the saved menu — showing the built-in one");
      applyItems(DEFAULT_SITE_MENU, false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // product counts per collection, so empty links stand out
    fetch(`${backend_url}/allproducts`)
      .then((r) => r.json())
      .then((products) => {
        const counts = {};
        (Array.isArray(products) ? products : []).forEach((p) => {
          if (p.isArchived) return;
          // a product counts once even when its category and subcategory share a name
          new Set([p.subcategory, p.category].map((k) => String(k || "").toLowerCase().trim()))
            .forEach((key) => { if (key) counts[key] = (counts[key] || 0) + 1; });
        });
        setProductCounts(counts);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savedJson = useMemo(() => JSON.stringify(savedItems), [savedItems]);
  const dirty = !loading && JSON.stringify(toSaved(rows)) !== savedJson;

  // warn before closing the tab with unsaved changes
  useEffect(() => {
    const onLeave = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [dirty]);

  const selected = rows.find((r) => r._id === selectedId) || null;
  const selectedIndex = rows.findIndex((r) => r._id === selectedId);

  const update = (id, patch) => setRows((rs) => rs.map((r) => (r._id === id ? { ...r, ...patch } : r)));
  const updateEntry = (id, entryId, patch) =>
    setRows((rs) => rs.map((r) => r._id !== id ? r : {
      ...r, dropdown: r.dropdown.map((d) => (d._id === entryId ? { ...d, ...patch } : d)),
    }));
  const select = (id) => { setSelectedId(id); setEntrySearch(""); setNewEntry(""); setShowAdvanced(false); };

  const addItem = () => {
    const row = { _id: nextId(), label: "NEW MENU", type: "dropdown", slug: "", category: "", hidden: false, open: true, dropdown: [] };
    setRows((rs) => {
      const priceLast = rs.length && rs[rs.length - 1].type === "price"; // keep Price Range last
      return priceLast ? [...rs.slice(0, -1), row, rs[rs.length - 1]] : [...rs, row];
    });
    select(row._id);
  };

  const removeItem = (r) =>
    setConfirm({
      title: `Remove "${r.label || "this item"}"?`,
      text: "It will disappear from the website menu after you save. Tip: switch it off instead if you may need it again.",
      yes: "Remove",
      onYes: () => {
        const i = rows.findIndex((x) => x._id === r._id);
        const rest = rows.filter((x) => x._id !== r._id);
        setRows(rest);
        select(rest[Math.min(i, rest.length - 1)]?._id || null);
      },
    });

  const addEntry = () => {
    const label = newEntry.trim();
    if (!selected || !label) return;
    update(selected._id, { dropdown: [...selected.dropdown, { _id: nextId(), label, slug: slugify(label), hidden: false }] });
    setNewEntry("");
    setEntrySearch("");
  };

  const discard = () =>
    setConfirm({
      title: "Discard your changes?",
      text: "The menu goes back to how it was last saved.",
      yes: "Discard",
      onYes: () => applyItems(savedItems, isCustom, updatedAt),
    });

  // Problems found before saving (shown in the save pop-up)
  const checkBeforeSave = () => {
    const bad = rows.find((r) => problemsFor(r).length);
    if (!bad) return null;
    select(bad._id);
    return `${bad.label || "A menu item"}: ${problemsFor(bad)[0]}`;
  };

  // Returns { ok } or { ok: false, message } — the save pop-up shows the result
  const save = async () => {
    setSaving(true);
    try {
      const items = toSaved(rows);
      const res  = await fetch(`${backend_url}/api/site-menu`, { method: "PUT", headers: authHeaders(), body: JSON.stringify({ items }) });
      if (res.status === 404) {
        return { ok: false, message: "The server doesn't have the Website Menu feature yet — deploy the latest backend, then save again." };
      }
      const data = await res.json().catch(() => ({}));
      // the login check answers 401, or 400 "Invalid Token"
      if (res.status === 401 || data.message === "Invalid Token") {
        return { ok: false, message: "Your login has expired — please log in again, then save." };
      }
      if (!data.success) return { ok: false, message: data.message || "Could not save the menu" };
      setSavedItems(items);
      setIsCustom(true);
      setUpdatedAt(data.updatedAt);
      return { ok: true };
    } catch {
      return { ok: false, message: "Could not reach the server — check your connection and try again." };
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = () =>
    setConfirm({
      title: "Go back to the original menu?",
      text: "Your custom menu is removed and the website shows the built-in menu again.",
      yes: "Reset menu",
      onYes: async () => {
        try {
          const res  = await fetch(`${backend_url}/api/site-menu`, { method: "DELETE", headers: authHeaders() });
          const data = await res.json();
          if (data.success) { toast.success("Website menu reset to the original"); load(); }
          else toast.error(data.message || "Could not reset");
        } catch { toast.error("Could not reset"); }
      },
    });

  const countFor = (slug) => productCounts[String(slug || "").toLowerCase()] || 0;
  const CountChip = ({ slug }) => {
    const n = countFor(slug);
    return (
      <span className={`wm-count ${n ? "" : "zero"}`} title={n ? `${n} product${n === 1 ? "" : "s"} in this collection` : "No products in this collection yet"}>
        {n === 1 ? "1 product" : `${n} products`}
      </span>
    );
  };

  if (loading) {
    return <div className="wm-page"><div className="wm-loading"><span className="wm-spinner" /> Loading menu…</div></div>;
  }

  const visibleEntries = selected?.type === "dropdown"
    ? selected.dropdown
        .map((d, j) => ({ d, j }))
        .filter(({ d }) => !entrySearch.trim() || d.label.toLowerCase().includes(entrySearch.trim().toLowerCase()))
    : [];
  const problems = selected ? problemsFor(selected) : [];

  return (
    <div className="wm-page">
      {/* ── Header ── */}
      <div className="wm-header">
        <div>
          <h1 className="wm-title">Website Menu</h1>
          <p className="wm-sub">Change the menu customers see at the top of the website. Only the words and links change — the design stays the same.</p>
        </div>
        <div className="wm-header-right">
          <span className={`wm-status ${isCustom ? "custom" : ""}`}>
            {isCustom ? `● Custom menu${updatedAt ? ` · saved ${new Date(updatedAt).toLocaleDateString()}` : ""}` : "● Original menu"}
          </span>
          {isCustom && <button className="wm-btn ghost" onClick={resetToDefault}>Reset to original</button>}
        </div>
      </div>

      {/* ── Live preview ── */}
      <section className="wm-preview">
        <div className="wm-preview-head">
          <span className="wm-eyebrow">How it looks on the website</span>
          <span className="wm-hint">Click a menu to edit it</span>
        </div>
        <div className="wm-preview-bar" role="group" aria-label="Website menu preview">
          {rows.map((r) => (
            <button key={r._id} type="button"
              className={`wm-preview-item ${r._id === selectedId ? "active" : ""} ${r.hidden ? "is-hidden" : ""}`}
              onClick={() => select(r._id)} title={r.hidden ? "Hidden — customers won't see this" : "Edit this menu"}>
              {r.label || "Untitled"}
              {r.type !== "link" && <Icon d={DOWN} size={12} />}
            </button>
          ))}
        </div>
        {selected?.type === "dropdown" && !selected.hidden && (
          <div className="wm-preview-drop">
            {selected.dropdown.filter((d) => !d.hidden).slice(0, 12).map((d) => (
              <span key={d._id}>{d.label || "Untitled"}</span>
            ))}
            {selected.dropdown.filter((d) => !d.hidden).length > 12 && (
              <span className="more">+{selected.dropdown.filter((d) => !d.hidden).length - 12} more</span>
            )}
            {!selected.dropdown.some((d) => !d.hidden) && <span className="more">No visible entries yet</span>}
          </div>
        )}
      </section>

      <div className="wm-layout">
        {/* ── Left: menu items ── */}
        <aside className="wm-side">
          <div className="wm-side-head">
            <h2>Menu items <span>{rows.length}</span></h2>
            <button className="wm-btn soft small" onClick={addItem}><Icon d={PLUS} size={14} /> Add</button>
          </div>
          <ul className="wm-items" aria-label="Menu items">
            {rows.map((r, i) => {
              const hasProblem = problemsFor(r).length > 0;
              return (
                <li key={r._id} className={`wm-item ${r._id === selectedId ? "active" : ""} ${r.hidden ? "is-hidden" : ""}`}
                  onClick={() => select(r._id)}>
                  <span className={`wm-type-dot t-${r.type}`}>{TYPES.find((t) => t.value === r.type)?.icon}</span>
                  <span className="wm-item-text">
                    <span className="wm-item-name">{r.label || "Untitled"}</span>
                    <span className="wm-item-meta">
                      {r.type === "dropdown" ? `${r.dropdown.length} entries` : r.type === "link" ? "Single link" : "Price filter"}
                      {r.hidden && " · Hidden"}
                      {hasProblem && <span className="wm-item-warn"> · Needs attention</span>}
                    </span>
                  </span>
                  <span className="wm-item-order" onClick={(e) => e.stopPropagation()}>
                    <button aria-label="Move up" title="Move left on the website" disabled={i === 0} onClick={() => setRows((rs) => move(rs, i, -1))}><Icon d={UP} size={14} /></button>
                    <button aria-label="Move down" title="Move right on the website" disabled={i === rows.length - 1} onClick={() => setRows((rs) => move(rs, i, 1))}><Icon d={DOWN} size={14} /></button>
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="wm-side-tip">The order here is the order on the website, left to right.</p>
        </aside>

        {/* ── Right: editor ── */}
        <main className="wm-editor">
          {!selected ? (
            <div className="wm-empty">Choose a menu item on the left, or add a new one.</div>
          ) : (
            <>
              <div className="wm-editor-head">
                <div>
                  <span className="wm-eyebrow">Editing · item {selectedIndex + 1} of {rows.length}</span>
                  <h2>{selected.label || "Untitled"}</h2>
                </div>
                <label className="wm-visibility">
                  <Switch on={!selected.hidden} onChange={(on) => update(selected._id, { hidden: !on })} label="Show on website" />
                  <span>{selected.hidden ? "Hidden from website" : "Showing on website"}</span>
                </label>
              </div>

              {problems.length > 0 && (
                <div className="wm-problem">⚠ {problems.join(" · ")}</div>
              )}

              <div className="wm-section">
                <label className="wm-label" htmlFor="wm-name">Menu name</label>
                <input id="wm-name" className={`wm-input ${!selected.label.trim() ? "invalid" : ""}`}
                  value={selected.label} placeholder="e.g. SAREES"
                  onChange={(e) => update(selected._id, { label: e.target.value })} />
                <p className="wm-help-text">Shown exactly as typed. Other menu names use CAPITAL letters.</p>
              </div>

              <div className="wm-section">
                <span className="wm-label">When customers click it</span>
                <div className="wm-types">
                  {TYPES.map((t) => (
                    <button key={t.value} type="button"
                      className={`wm-type-card ${selected.type === t.value ? "active" : ""}`}
                      onClick={() => update(selected._id, { type: t.value })}>
                      <span className={`wm-type-icon t-${t.value}`}>{t.icon}</span>
                      <span className="wm-type-title">{t.title}</span>
                      <span className="wm-type-text">{t.text}</span>
                    </button>
                  ))}
                </div>
              </div>

              {selected.type === "link" && (
                <div className="wm-section">
                  <label className="wm-label" htmlFor="wm-link">Collection it opens</label>
                  <div className={`wm-slug ${!selected.slug ? "invalid" : ""}`}>
                    <em>aavryaafabhub.com{PREFIX}</em>
                    <input id="wm-link" value={selected.slug} placeholder="e.g. vintage-collection"
                      onChange={(e) => update(selected._id, { slug: slugify(e.target.value) })} />
                    <CountChip slug={selected.slug} />
                  </div>
                  <p className="wm-help-text">Shows every product whose <b>Subcategory</b> is this name.</p>
                </div>
              )}

              {selected.type === "price" && (
                <div className="wm-section">
                  <div className="wm-info">₹ Customers choose a minimum and maximum price, and see matching products. Nothing else to set up.</div>
                </div>
              )}

              {selected.type === "dropdown" && (
                <div className="wm-section">
                  <div className="wm-entries-head">
                    <span className="wm-label">Dropdown entries <span className="wm-pill">{selected.dropdown.length}</span></span>
                    {selected.dropdown.length > 6 && (
                      <input className="wm-input wm-search" placeholder="🔍 Find an entry…"
                        value={entrySearch} onChange={(e) => setEntrySearch(e.target.value)} />
                    )}
                  </div>

                  <div className="wm-quick-add">
                    <input className="wm-input" placeholder="New entry name, e.g. Linen Saree"
                      value={newEntry} onChange={(e) => setNewEntry(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addEntry()} />
                    <button className="wm-btn primary" onClick={addEntry} disabled={!newEntry.trim()}>
                      <Icon d={PLUS} size={14} /> Add entry
                    </button>
                  </div>
                  {newEntry.trim() && (
                    <p className="wm-help-text">Will open <code>{PREFIX}{slugify(newEntry)}</code> — you can change the link after adding.</p>
                  )}

                  <p className="wm-help-text">Entries show on the website in this order. Use ↑ ↓ to move an entry up or down the list.</p>
                  <ul className="wm-entries">
                    {visibleEntries.map(({ d, j }) => (
                      <li key={d._id} className={`wm-entry ${d.hidden ? "is-hidden" : ""}`}>
                        {/* Position in the dropdown + move up/down */}
                        <span className="wm-entry-pos" title={`Position ${j + 1} in the dropdown`}>{j + 1}</span>
                        <span className="wm-entry-order">
                          <button aria-label="Move up" title={entrySearch ? "Clear the search to reorder" : "Move up one place"}
                            disabled={j === 0 || !!entrySearch} onClick={() => update(selected._id, { dropdown: move(selected.dropdown, j, -1) })}><Icon d={UP} size={13} /></button>
                          <button aria-label="Move down" title={entrySearch ? "Clear the search to reorder" : "Move down one place"}
                            disabled={j === selected.dropdown.length - 1 || !!entrySearch} onClick={() => update(selected._id, { dropdown: move(selected.dropdown, j, 1) })}><Icon d={DOWN} size={13} /></button>
                        </span>
                        <div className="wm-entry-main">
                          <input className={`wm-input wm-entry-name ${!d.label.trim() ? "invalid" : ""}`} value={d.label}
                            aria-label="Entry name" placeholder="Entry name"
                            onChange={(e) => {
                              const label = e.target.value;
                              const auto = !d.slug || d.slug === slugify(d.label); // keep link in step for new entries
                              updateEntry(selected._id, d._id, auto ? { label, slug: slugify(label) } : { label });
                            }} />
                          <div className={`wm-slug compact ${!d.slug ? "invalid" : ""}`}>
                            <em>{PREFIX}</em>
                            <input value={d.slug} aria-label="Collection link" placeholder="link-name"
                              onChange={(e) => updateEntry(selected._id, d._id, { slug: slugify(e.target.value) })} />
                          </div>
                        </div>
                        <CountChip slug={d.slug} />
                        <Switch small on={!d.hidden} label={d.hidden ? "Hidden — click to show" : "Showing — click to hide"}
                          onChange={(on) => updateEntry(selected._id, d._id, { hidden: !on })} />
                        <button className="wm-icon-btn danger" aria-label={`Remove ${d.label}`} title="Remove entry"
                          onClick={() => update(selected._id, { dropdown: selected.dropdown.filter((x) => x._id !== d._id) })}>
                          <Icon d={TRASH} size={15} />
                        </button>
                      </li>
                    ))}
                    {selected.dropdown.length === 0 && <li className="wm-entries-empty">No entries yet — add the first one above.</li>}
                    {selected.dropdown.length > 0 && visibleEntries.length === 0 && <li className="wm-entries-empty">No entry matches “{entrySearch}”.</li>}
                  </ul>

                  <button className="wm-advanced-toggle" onClick={() => setShowAdvanced((v) => !v)}>
                    {showAdvanced ? "▾" : "▸"} Advanced
                  </button>
                  {showAdvanced && (
                    <div className="wm-advanced">
                      <label className="wm-label" htmlFor="wm-cat">Product category</label>
                      <input id="wm-cat" className="wm-input" value={selected.category} placeholder={slugify(selected.label)}
                        onChange={(e) => update(selected._id, { category: slugify(e.target.value) })} />
                      <p className="wm-help-text">The Category picked in Add/Edit Product for these entries (e.g. <code>fabrics</code>, <code>sarees</code>). Leave as is unless you're creating a new section.</p>
                    </div>
                  )}
                </div>
              )}

              <div className="wm-editor-foot">
                <button className="wm-btn danger-outline" onClick={() => removeItem(selected)}>
                  <Icon d={TRASH} size={14} /> Remove this menu item
                </button>
              </div>
            </>
          )}
        </main>
      </div>

      {/* ── Save bar ── */}
      <SaveBar dirty={dirty} saving={saving} onSave={save} onDiscard={discard}
        check={checkBeforeSave} what="the website menu" />

      {confirm && (
        <div className="wm-modal-overlay" onClick={() => setConfirm(null)}>
          <div className="wm-modal" role="dialog" aria-label={confirm.title} onClick={(e) => e.stopPropagation()}>
            <h3>{confirm.title}</h3>
            <p>{confirm.text}</p>
            <div className="wm-modal-actions">
              <button className="wm-btn ghost" onClick={() => setConfirm(null)}>Cancel</button>
              <button className="wm-btn danger" onClick={() => { confirm.onYes(); setConfirm(null); }}>{confirm.yes || "Yes"}</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export { toEditor, toSaved, slugify };
export default WebsiteMenu;
